import { Container } from 'gu-ui-kit'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styles from 'modules/Statistics/subcomponents/Layout/Table/statistics-table.module.scss'
import { formatByLocale } from 'utils/formatMoney'
import { useSelector } from 'react-redux'
import CellStyles from 'modules/Statistics/Account/cells.module.scss'
import clsx from 'clsx'
import { StatusBadge } from 'modules/Statistics/subcomponents/StatusBadge'
import { StateIcons } from 'modules/Statistics/subcomponents/StateIcons'

export const useTableConfig = ({
  filters = { include_vat: true },
  summary,
  sorting,
  setSorting,
  columnVisibility,
  setColumnVisibility,
  network,
  className = '',
}) => {
  const {
    i18n,
    user: { id: userId },
  } = useSelector(({ app }) => app)

  const { t } = useTranslation()
  const columns = useMemo(() => {
    let commonColumns

    switch (network) {
      case 'vk':
        return [
          {
            id: 'ad.id',
            header: t('statistics::table.columns.header.ad'),
            accessorKey: 'ad.id',
            //sticky,
            minWidth: 255,
            enableHiding: false,
            // eslint-disable-next-line react/prop-types
            cell: ({
              row: {
                original: {
                  ad: { id },
                },
              },
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={id}
              >
                <div className={'overflow'}>
                  <span className={styles['login']}>{id}</span>
                </div>
              </Container>
            ),
          },
          {
            id: 'ad.status',
            header: t('statistics::table.columns.header.status'),
            accessorKey: 'ad.status',
            width: 130,
            enableHiding: false,
            cell: ({
              row: {
                original: {
                  ad: { status },
                },
              },
            }) => {
              return !status ? '—' : <StatusBadge status={status} />
            },
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}:{' '}
                {t('statistics::table.footer.ad', { count: table.getRowCount() })}
              </b>
            ),
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            minWidth: 120,
            width: 120,
            cell: ({
              row: {
                original: { impressions },
              },
            }) => (impressions === null ? '—' : formatByLocale(impressions)),
            footer: () => (
              <b>{summary?.impressions === null ? '—' : formatByLocale(summary?.impressions)}</b>
            ),
          },
          {
            id: 'cost',
            header: t(
              `statistics::table.columns.header.${filters.include_vat ? 'cost_with_vat' : 'cost'}`,
            ),
            accessorKey: filters.include_vat ? 'cost_with_vat' : 'cost',
            minWidth: 150,
            cell: ({
              row: {
                original: { cost, cost_with_vat },
              },
            }) => {
              const val = filters.include_vat ? cost_with_vat : cost

              return val === null
                ? '—'
                : formatByLocale(val, i18n, {
                    maximumFractionDigits: 2,
                  })
            },
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'] === null
                  ? '—'
                  : formatByLocale(
                      summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'],
                      i18n,
                      {
                        maximumFractionDigits: 2,
                      },
                    )}
              </b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { clicks },
              },
            }) => (clicks === null ? '—' : formatByLocale(clicks)),
            footer: () => <b>{summary?.clicks === null ? '—' : formatByLocale(summary?.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',
            minWidth: 80,
            width: 100,
            cell: ({
              row: {
                original: { ctr },
              },
            }) =>
              ctr === null
                ? '—'
                : formatByLocale(ctr, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.ctr === null
                  ? '—'
                  : formatByLocale(summary?.ctr, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
          {
            id: 'avg_click_price',
            header: t(
              `statistics::table.columns.header.${
                filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
              }`,
            ),
            accessorKey: filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price',
            minWidth: 160,
            cell: ({
              row: {
                original: { avg_click_price_with_vat, avg_click_price },
              },
            }) => {
              const val = filters.include_vat ? avg_click_price_with_vat : avg_click_price

              return val === null
                ? '—'
                : formatByLocale(val, i18n, {
                    maximumFractionDigits: 2,
                  })
            },
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'] ===
                null
                  ? '—'
                  : formatByLocale(
                      summary?.[
                        filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
                      ],
                      i18n,
                      { maximumFractionDigits: 2 },
                    )}
              </b>
            ),
          },
        ]

      case 'googleads':
      case 'yandexdirect':
        commonColumns = [
          {
            id: 'ad.id',
            header: t('statistics::table.columns.header.ad'),
            accessorKey: 'ad.id',
            className: 'ad-id-cell sticky',
            minWidth: 190,
            enableHiding: false,
            footerColSpan: 2,
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                original: {
                  ad: { id },
                },
              },
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={id}
              >
                <div className={'overflow'}>
                  <span className={styles['login']}>{id}</span>
                </div>
              </Container>
            ),
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}:{' '}
                {t('statistics::table.footer.ad', { count: table.getRowCount() })}
              </b>
            ),
          },
          {
            id: 'ad.state',
            header: t('statistics::table.columns.header.state'),
            accessorKey: 'ad.state',
            width: 130,
            enableHiding: false,
            footerColSpan: 0,
            cell: ({
              row: {
                original: { ad },
              },
            }) => <StateIcons state={ad?.state?.toLowerCase()} />,
            footer: () => <></>,
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            minWidth: 120,
            width: 120,
            cell: ({
              row: {
                original: { impressions },
              },
            }) => (impressions === null ? '—' : formatByLocale(impressions)),
            footer: () => (
              <b>{summary?.impressions === null ? '—' : formatByLocale(summary?.impressions)}</b>
            ),
          },
          {
            id: 'cost',
            header: t(
              `statistics::table.columns.header.${filters.include_vat ? 'cost_with_vat' : 'cost'}`,
            ),
            accessorKey: filters.include_vat ? 'cost_with_vat' : 'cost',
            minWidth: 150,
            cell: ({
              row: {
                original: { cost },
              },
            }) =>
              cost === null
                ? '—'
                : formatByLocale(cost, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'] === null
                  ? '—'
                  : formatByLocale(
                      summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'],
                      i18n,
                      {
                        maximumFractionDigits: 2,
                      },
                    )}
              </b>
            ),
          },
          {
            id: 'revenue',
            header: t('statistics::table.columns.header.revenue_by_target'),
            accessorKey: 'revenue',
            minWidth: 100,
            width: 140,
            cell: ({
              row: {
                original: { revenue },
              },
            }) => (revenue === null ? '—' : formatByLocale(revenue)),
            footer: () => (
              <b>{summary?.revenue === null ? '—' : formatByLocale(summary?.revenue)}</b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            minWidth: 80,
            width: 100,
            cell: ({
              row: {
                original: { clicks },
              },
            }) => (clicks === null ? '—' : formatByLocale(clicks)),
            footer: () => <b>{summary?.clicks === null ? '—' : formatByLocale(summary?.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            accessorKey: 'ctr',
            minWidth: 70,
            width: 70,
            cell: ({
              row: {
                original: { ctr },
              },
            }) =>
              ctr === null
                ? '—'
                : formatByLocale(ctr, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.ctr === null
                  ? '—'
                  : formatByLocale(summary?.ctr, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
          {
            id: 'avg_click_price',
            header: t(
              `statistics::table.columns.header.${
                filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
              }`,
            ),
            accessorKey: filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price',
            minWidth: 165,
            cell: ({
              row: {
                original: { avg_click_price_with_vat, avg_click_price },
              },
            }) => {
              const val = filters.include_vat ? avg_click_price_with_vat : avg_click_price

              return val === null
                ? '—'
                : formatByLocale(val, i18n, {
                    maximumFractionDigits: 2,
                  })
            },
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'] ===
                null
                  ? '—'
                  : formatByLocale(
                      summary?.[
                        filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
                      ],
                      i18n,
                      { maximumFractionDigits: 2 },
                    )}
              </b>
            ),
          },
          {
            id: 'conversions',
            header: t('statistics::table.columns.header.conversions'),
            accessorKey: 'conversions',
            minWidth: 120,
            width: 120,
            cell: ({
              row: {
                original: { conversions },
              },
            }) => (conversions === null ? '—' : formatByLocale(conversions)),
            footer: () => (
              <b>{summary?.conversions === null ? '—' : formatByLocale(summary?.conversions)}</b>
            ),
          },
          {
            id: 'conversion_rate',
            header: t('statistics::table.columns.header.conversion_rate'),
            accessorKey: 'conversion_rate',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { conversion_rate },
              },
            }) =>
              conversion_rate === null
                ? '—'
                : formatByLocale(conversion_rate, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.conversion_rate === null
                  ? '—'
                  : formatByLocale(summary?.conversion_rate, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
        ]

        return summary?.google_analytics_metrics && summary?.google_analytics_metrics
          ? commonColumns.concat({
              header: <span className={CellStyles['header-cell__super']}>Google Analytics</span>,
              footer: () => <span></span>,
              canSort: false,
              id: 'google_analytics',
              className: clsx('grouped-cell', CellStyles['google_analytics_cell']),
              columns: [
                'bounce_rate',
                'bounces',
                'drr',
                'new_users',
                'pageviews',
                'roi',
                'session_duration',
                'sessions',
                'transaction_revenue',
                'transactions',
                'users',
              ].map((columnName) => ({
                id: 'google_analytics_metrics.' + columnName,
                header: t('statistics::table.columns.ga_header.' + columnName),
                minWidth: 100,
                width: 150,
                accessorKey: 'google_analytics_metrics.' + columnName,
                className: clsx('grouped-cell', CellStyles['google_analytics_cell']),
                cell: (cell) =>
                  !cell.getValue()
                    ? '—'
                    : formatByLocale(cell.getValue(), i18n, {
                        maximumFractionDigits: 2,
                      }),
                footer: () => (
                  <b>
                    {summary?.google_analytics_metrics?.[columnName] === null
                      ? '—'
                      : formatByLocale(summary?.google_analytics_metrics?.[columnName], i18n, {
                          maximumFractionDigits: 2,
                        })}
                  </b>
                ),
              })),
            })
          : commonColumns
    }
  }, [filters?.include_vat, i18n, userId, network, summary])

  return {
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    showHeader: true,
    showFooter: true,
    enableRowSelection: false,
    className,
  }
}
